/**
 * Функции форматирования различных типов данных
 */

/**
 * Форматирование числа с разделителями и знаками после запятой
 * 
 * @param {number} value - Число для форматирования
 * @param {number} decimals - Количество знаков после запятой
 * @param {string} decPoint - Разделитель десятичной части
 * @param {string} thousandsSep - Разделитель групп разрядов
 * @returns {string} Отформатированное число
 */
export const formatNumber = (value, decimals = 2, decPoint = ',', thousandsSep = ' ') => {
  // Обработка NaN и Infinity
  if (!isFinite(value)) {
    return String(value);
  }
  
  const num = Number(value).toFixed(decimals);
  const parts = num.toString().split('.');
  
  // Форматирование целой части с разделителями
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  
  // Соединение целой и дробной части
  return parts.join(decPoint);
};


/**
 * Форматирование валюты
 * 
 * @param {number} value - Сумма для форматирования
 * @param {number|string} digits - Количество знаков после запятой или код валюты
 * @param {string} locale - Локаль для форматирования (ru-RU, en-US и т.д.)
 * @returns {string} Отформатированная сумма с символом валюты
 */
export const formatCurrency = (value, digits = 0, locale = 'ru-RU') => {
  // Если digits передан как число, то это количество знаков после запятой
  // Если digits передан как строка, то это код валюты
  if (typeof digits === 'string' && digits.length === 3) {
    // Вызов в старом формате: value, currency, locale
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: digits,
      maximumFractionDigits: 2
    }).format(value);
  } else {
    // Вызов в новом формате: value, digits, locale
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(value);
  }
};

/**
 * Получение символа валюты
 * 
 * @param {string} currency - Код валюты (RUB, USD, EUR и т.д.)
 * @param {string} locale - Локаль для форматирования (ru-RU, en-US и т.д.)
 * @returns {string} Символ валюты
 */
export const getCurrencySymbol = (currency = 'RUB', locale = 'ru-RU') => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  
  const parts = formatter.formatToParts(0);
  const symbol = parts.find(part => part.type === 'currency')?.value || currency;
  
  return symbol;
};

/**
 * Форматирование процентов
 * 
 * @param {number} value - Значение в процентах
 * @param {number} decimals - Количество знаков после запятой
 * @returns {string} Отформатированное значение с символом %
 */
export const formatPercent = (value, decimals = 2) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value / 100);
};

/**
 * Форматирование даты
 * 
 * @param {Date|string} date - Дата для форматирования
 * @param {string} format - Формат ('date', 'datetime', 'time', 'short', 'long', 'full')
 * @param {string} locale - Локаль для форматирования
 * @returns {string} Отформатированная дата
 */
export const formatDate = (date, format = 'date', locale = 'ru-RU') => {
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Проверка валидности даты
  if (isNaN(dateObj.getTime())) {
    return 'Некорректная дата';
  }
  
  // Настройки для разных форматов
  const options = {
    date: { day: '2-digit', month: '2-digit', year: 'numeric' },
    datetime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
    time: { hour: '2-digit', minute: '2-digit' },
    short: { day: 'numeric', month: 'short', year: 'numeric' },
    long: { day: 'numeric', month: 'long', year: 'numeric' },
    full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
  };
  
  return new Intl.DateTimeFormat(locale, options[format] || options.date).format(dateObj);
};

/**
 * Форматирование отрезка времени
 * 
 * @param {number} milliseconds - Время в миллисекундах
 * @param {string} format - Формат ('short', 'medium', 'long')
 * @returns {string} Отформатированное время
 */
export const formatDuration = (milliseconds, format = 'medium') => {
  // Расчет компонентов времени
  const seconds = Math.floor(milliseconds / 1000) % 60;
  const minutes = Math.floor(milliseconds / (1000 * 60)) % 60;
  const hours = Math.floor(milliseconds / (1000 * 60 * 60)) % 24;
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
  
  // Форматы вывода
  switch (format) {
    case 'short':
      if (days > 0) return `${days}д ${hours}ч`;
      if (hours > 0) return `${hours}ч ${minutes}м`;
      if (minutes > 0) return `${minutes}м ${seconds}с`;
      return `${seconds}с`;
      
    case 'long':
      const parts = [];
      if (days > 0) parts.push(`${days} ${pluralize(days, 'день', 'дня', 'дней')}`);
      if (hours > 0 || days > 0) parts.push(`${hours} ${pluralize(hours, 'час', 'часа', 'часов')}`);
      if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes} ${pluralize(minutes, 'минута', 'минуты', 'минут')}`);
      parts.push(`${seconds} ${pluralize(seconds, 'секунда', 'секунды', 'секунд')}`);
      return parts.join(' ');
      
    case 'medium':
    default:
      if (days > 0) return `${days}д ${hours}ч ${minutes}м`;
      if (hours > 0) return `${hours}ч ${minutes}м ${seconds}с`;
      if (minutes > 0) return `${minutes}м ${seconds}с`;
      return `${seconds}с`;
  }
};

/**
 * Форматирование размера файла
 * 
 * @param {number} bytes - Размер в байтах
 * @param {number} decimals - Количество знаков после запятой
 * @returns {string} Отформатированный размер с единицей измерения
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Б';
  
  const k = 1024;
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ', 'ЭБ', 'ЗБ', 'ЙБ'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Склонение существительных в зависимости от числа
 * 
 * @param {number} count - Число для склонения
 * @param {string} one - Форма для 1
 * @param {string} few - Форма для 2-4
 * @param {string} many - Форма для 5-20, 0
 * @returns {string} Правильная форма слова
 */
export const pluralize = (count, one, few, many) => {
  const absCount = Math.abs(count);
  const lastDigit = absCount % 10;
  const lastTwoDigits = absCount % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return many;
  }
  
  if (lastDigit === 1) {
    return one;
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return few;
  }
  
  return many;
};

/**
 * Сокращение текста до указанной длины
 * 
 * @param {string} text - Исходный текст
 * @param {number} maxLength - Максимальная длина
 * @param {string} ending - Окончание для сокращенного текста
 * @returns {string} Сокращенный текст
 */
export const truncateText = (text, maxLength = 100, ending = '...') => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  
  // Находим последнее пробельное значение перед maxLength
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > 0) {
    return truncated.substring(0, lastSpace) + ending;
  }
  
  return truncated + ending;
};